import React, { useRef, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

/**
 * Reusable image uploader for admin settings.
 * Shows a preview + URL field + "Uploader" button.
 * Calls POST /api/upload/image and bubbles up the returned public URL via onChange.
 *
 * Props:
 *   value      - current URL (string)
 *   onChange   - (newUrl: string) => void
 *   label      - field label (optional, the parent usually renders one)
 *   accept     - file input accept attr (default: image/*)
 *   testIdPrefix - data-testid prefix for buttons (e.g. "hero-bg")
 */
const ImageUploader = ({ value, onChange, accept = 'image/*', testIdPrefix = 'image' }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE}/upload/image`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      onChange(data.url);
    } catch (err) {
      setError(err.message || "Erreur d'upload");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-uploader`}>
      <div className="flex gap-3 items-start">
        {value ? (
          <div className="relative w-24 h-24 border border-border bg-surface flex-shrink-0 overflow-hidden group">
            <img src={value} alt="aperçu" className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            <button
              type="button"
              onClick={() => onChange('')}
              className="absolute top-1 right-1 bg-white/90 hover:bg-red-600 hover:text-white p-1 transition-colors opacity-0 group-hover:opacity-100"
              title="Retirer l'image"
              data-testid={`${testIdPrefix}-clear-btn`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="w-24 h-24 border border-dashed border-border bg-surface flex items-center justify-center flex-shrink-0 text-muted-foreground">
            <ImageIcon className="w-6 h-6" />
          </div>
        )}
        <div className="flex-1 min-w-0 space-y-2">
          <Input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="URL de l'image (https://...)"
            className="rounded-none text-xs font-mono"
            data-testid={`${testIdPrefix}-url-input`}
          />
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              onChange={handleFile}
              className="hidden"
              data-testid={`${testIdPrefix}-file-input`}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-none"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid={`${testIdPrefix}-upload-btn`}
            >
              {uploading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Upload en cours...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" />Uploader une image</>
              )}
            </Button>
            <span className="text-xs text-muted-foreground">JPG, PNG, WebP, SVG · max 10 Mo</span>
          </div>
          {error && <p className="text-xs text-red-600" data-testid={`${testIdPrefix}-error`}>{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default ImageUploader;
